import {castArray} from 'lodash';
import {GeneratedValue, GeneratedSum, GeneratedFirst, ArrayCountOptions, ArrayFilter} from '@/model/types';
import {ValueResolver, ValueResolverOptions} from '../types';
import {recursiveReplaceIndex} from '../utils/object';

export const resolveGenerated = <T>(
  reference: GeneratedValue,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
) => {
  if ('$sum' in reference.$generated) {
    if ('fields' in reference.$generated.$sum) {
      return computeFieldsSum(reference.$generated.$sum, resolver, options);
    } else {
      return computeArraySum(reference.$generated.$sum, resolver, options);
    }
  }

  if ('$first' in reference.$generated) {
    return computeFirst(reference.$generated.$first, resolver, options);
  }

  throw new Error('Unsupported generated value');
};

const computeFieldsSum = <T>(
  {fields, property, keepDecimals = false}: Extract<GeneratedSum['$sum'], {fields: string[]}>,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
) => {
  const {transform = input => input as T} = options;
  const values = fields.flatMap(field =>
    castArray(resolver({$ref: field, property}, {...options, keepReference: false, keepDomainReference: false})),
  );
  const sum = values.reduce((sum, value) => sum + Number(value), 0);
  return transform(keepDecimals ? sum.toFixed(2) : sum.toFixed()) as T;
};

const computeArraySum = <T>(
  {array: {count}}: Extract<GeneratedSum['$sum'], {array: unknown}>,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
) => {
  const {transform = input => input as T} = options;
  const values = count.map(field => {
    if (typeof field === 'string') {
      const arrayCount = Number(resolver({$ref: `_${field}[count]`}));
      return isNaN(arrayCount) ? 0 : arrayCount;
    }

    return computeArrayCount(field, resolver, options);
  });

  return transform(values.reduce((sum, value) => sum + value, 0).toFixed()) as T;
};

const computeArrayCount = <T>(
  array: ArrayCountOptions,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): number => {
  const {array: arrayName, filters = [], groupBy} = array;
  const arrayCount = Number(resolver({$ref: `_${arrayName}[count]`}));

  if (isNaN(arrayCount)) {
    return 0;
  }

  const groupedBy = new Set();

  const rows = Array.from({length: arrayCount}, (_, index) => {
    return resolver({$ref: `_${arrayName}#${index}[fields]`}).split(',');
  }).filter((rowFields, index) => {
    const indexedFilters = recursiveReplaceIndex(filters, index);

    for (const filter of indexedFilters) {
      if (rowFields.includes(filter.field)) {
        if (!applyFilter(filter, resolver, options)) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (groupBy) {
      if (groupedBy.has(resolver({$ref: `${groupBy}#${index}`}, options))) {
        return false;
      }
      groupedBy.add(resolver({$ref: `${groupBy}#${index}`}, options));
    }

    return true;
  });

  return rows.length;
};

const computeFirst = <T>(
  {array: arrayName, field, filters = []}: GeneratedFirst['$first'],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): T => {
  const {transform = input => input as T} = options;
  const arrayCount = Number(resolver({$ref: `_${arrayName}[count]`}));

  if (isNaN(arrayCount)) {
    return transform('');
  }

  for (let index = 0; index < arrayCount; index++) {
    const indexedFilters = recursiveReplaceIndex(filters, index);
    const matchesAllFilters = indexedFilters.every(filter => applyFilter(filter, resolver, options));

    if (matchesAllFilters) {
      const indexedField = recursiveReplaceIndex(field, index);
      return transform(resolver({$ref: indexedField})) as T;
    }
  }

  return transform('');
};

export const applyFilter = <T>(
  filter: ArrayFilter,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): boolean => {
  const fieldValue = resolver({$ref: filter.field}, {visibilitySet: options.visibilitySet});
  const targetValue = resolver(filter.value, {visibilitySet: options.visibilitySet});

  switch (filter.operator) {
    case '=':
      return fieldValue === targetValue;
    case '!=':
      return fieldValue !== targetValue;
    case 'in':
      return targetValue.includes(fieldValue);
    case 'not in':
      return !targetValue.includes(fieldValue);
  }
};
